"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { api } from "@/shared/lib/api";

/**
 * Получает short-lived JWT для desktop-клиента (Tauri).
 *
 * Зачем: браузерные запросы используют HttpOnly `sid` cookie, который JS прочитать
 * не может. Tauri (Rust reqwest) НЕ разделяет cookie-jar с webview, поэтому ему
 * нужен явный Bearer-токен.
 *
 * Хук:
 * - запрашивает `/me/desktop-token` при mount (если юзер залогинен)
 * - переисщёт за 60s до истечения (типичный TTL — 60 мин)
 * - возвращает `getToken()` который вернёт текущий валидный (или дождётся свежий)
 *
 * Использование:
 * ```ts
 * const { getToken } = useDesktopToken();
 * const authToken = await getToken();
 * await invokeTauri("set_active_match", { req: { ..., authToken } });
 * ```
 */
export function useDesktopToken() {
  const [token, setToken] = useState<string | null>(null);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingFetchRef = useRef<Promise<string | null> | null>(null);

  const doFetch = useCallback(async (): Promise<string | null> => {
    try {
      const resp = await api.get<{ token: string; expiresIn: number }>(
        "/me/desktop-token",
      );
      const data = resp.data;
      if (!data?.token) return null;

      setToken(data.token);

      // Сохраняем токен в Tauri Rust-state чтобы фоновый поллер
      // `/me/active-match` мог работать даже когда webview в фоне.
      try {
        const { checkIsTauri, invokeTauri } = await import("@/shared/lib/tauri");
        if (checkIsTauri()) {
          await invokeTauri("store_auth_context", {
            req: {
              authToken: data.token,
              backendUrl: window.location.origin,
            },
          });
        }
      } catch (e) {
        // не критично — поллер просто не активирует пока токена нет
        console.warn("[useDesktopToken] store_auth_context failed", e);
      }

      // Schedule refresh 60s before expiry; expiresIn is in seconds.
      const refreshInMs = Math.max(30, data.expiresIn - 60) * 1000;
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => {
        void doFetch();
      }, refreshInMs);

      return data.token;
    } catch (e) {
      // Likely 401 — user is not authenticated. Caller decides what to do.
      console.warn("[useDesktopToken] fetch failed", e);
      return null;
    }
  }, []);

  useEffect(() => {
    void doFetch();
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [doFetch]);

  /**
   * Возвращает актуальный токен. Если ещё не загрузился — ждёт первый fetch.
   * Не делает повторных запросов параллельно — все вызовы во время загрузки
   * ждут одной и той же promise.
   */
  const getToken = useCallback(async (): Promise<string | null> => {
    if (token) return token;
    if (pendingFetchRef.current) return pendingFetchRef.current;
    pendingFetchRef.current = doFetch().finally(() => {
      pendingFetchRef.current = null;
    });
    return pendingFetchRef.current;
  }, [token, doFetch]);

  return { token, getToken };
}
